import { AdvancedOptions, type FilterValues } from "./search/AdvancedOptions";
import { useState, useEffect, useCallback, useRef } from "react";
import { RefreshCw, Users, MessageSquare, Filter } from "lucide-react";
import {
  apiService,
  type ChatHistory,
} from "../services/api.service";
import { useHeaderActions } from "../context/HeaderActionsContext";
import { isNotepadSession } from "@/lib/notepad-transcript";
import { localDayEnd, localDayStart } from "@/lib/local-day-range";
import { sessionTime, type ChatHistoryOrder } from "@/lib/session-time";
import { conversationUsers } from "@/lib/conversation-users";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { UserList } from "./ui/user-list";
import {
  ChatPanel,
  type UserOption,
  type Session,
  type Lesson,
} from "./conversation-overview";

const SESSIONS_PAGE_SIZE = 25;

/** The filters a listed page was loaded with. */
interface ListQuery {
  lessonId?: string;
  startDate?: string;
  endDate?: string;
}

export const ConversationOverview = () => {
  const [userFilters, setUserFilters] = useState<FilterValues>({});
  const [organizations, setOrganizations] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => { apiService.getOrganizations().then(setOrganizations).catch(() => {}); }, []);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [chatHistory, setChatHistory] = useState<ChatHistory | null>(null);
  const [activeSessionIndex, setActiveSessionIndex] = useState(0);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [_error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [nextToken, setNextToken] = useState<string | null>(null);

  // Session filters
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
  // What the list is ordered and date-filtered by. The studio can answer
  // "saved" with "started" (chatHistory.order_by) until its index is ready.
  const [orderBy, setOrderBy] = useState<ChatHistoryOrder>("saved");
  const listedOrder: ChatHistoryOrder = chatHistory?.order_by ?? orderBy;
  // Load more pages on with the filters the list was loaded with, not with
  // inputs edited since and not applied: the next_token belongs to that query.
  const listedQuery = useRef<ListQuery>({});
  // Each new list (user, Apply, Clear, order) gets a new number. A response
  // for an older list is dropped, so it cannot land in the new one.
  const listGeneration = useRef(0);
  const userGeneration = useRef(0);

  const { setActions } = useHeaderActions();

  const loadUsers = useCallback(async () => {
    const generation = ++userGeneration.current;
    try {
      setIsLoadingUsers(true);
      const [teachersData, studentsData] = await Promise.all([
        apiService.getTeachers(userFilters.organization_id || undefined),
        apiService.getStudents(userFilters.organization_id || undefined),
      ]);

      if (generation === userGeneration.current) setUsers(conversationUsers(teachersData, studentsData));
    } catch (err) {
      if (generation === userGeneration.current) setError("Failed to load users");
    } finally {
      if (generation === userGeneration.current) setIsLoadingUsers(false);
    }
  }, [userFilters.organization_id]);

  const hasActiveFilters = !!(selectedLesson || startDate || endDate);
  const totalUsers = users.length;
  const loadedSessions = chatHistory?.sessions.length ?? 0;

  // Set header actions
  useEffect(() => {
    setActions(
      <div className="flex items-center gap-2">
        <Badge
          variant="outline"
          className="gap-1.5 rounded-full px-3 py-1 text-xs"
        >
          <Users className="h-3 w-3" />
          {totalUsers} users
        </Badge>
        <Badge
          variant="outline"
          className="gap-1.5 rounded-full px-3 py-1 text-xs"
        >
          <MessageSquare className="h-3 w-3" />
          {loadedSessions} session{loadedSessions === 1 ? "" : "s"}
          {nextToken ? "+" : ""}
        </Badge>
        {hasActiveFilters && (
          <Badge className="gap-1.5 rounded-full border border-lime-600/20 bg-lime-600/10 px-3 py-1 text-xs text-lime-900">
            <Filter className="h-3 w-3" />
            Filters active
          </Badge>
        )}
        <Button
          variant="outline"
          size="icon"
          onClick={loadUsers}
          disabled={isLoadingUsers}
          className="h-8 w-8"
        >
          <RefreshCw
            className={`h-4 w-4 ${isLoadingUsers ? "animate-spin" : ""}`}
          />
        </Button>
      </div>,
    );
    return () => setActions(null);
  }, [
    setActions,
    totalUsers,
    loadedSessions,
    nextToken,
    hasActiveFilters,
    isLoadingUsers,
    loadUsers,
  ]);

  useEffect(() => {
    setUsers([]);
    setSelectedUserId(null);
    setChatHistory(null);
    loadUsers();
    return () => { userGeneration.current += 1; };
  }, [loadUsers]);

  useEffect(() => {
    if (selectedUserId) {
      loadChatHistory(selectedUserId);
    } else {
      listGeneration.current += 1;
      setChatHistory(null);
    }
  }, [selectedUserId]);

  const loadChatHistory = async (
    userId: string,
    resetFilters = false,
    order: ChatHistoryOrder = orderBy,
  ) => {
    const generation = ++listGeneration.current;
    const query: ListQuery = resetFilters
      ? {}
      : {
          lessonId: selectedLesson?.lesson_id.toString(),
          startDate: localDayStart(startDate),
          endDate: localDayEnd(endDate),
        };
    try {
      setIsLoadingChat(true);
      setIsLoadingMore(false);
      setNextToken(null);
      const data = await apiService.getChatHistory(userId, {
        limit: SESSIONS_PAGE_SIZE,
        order_by: order,
        lesson_id: query.lessonId,
        start_date: query.startDate,
        end_date: query.endDate,
      });
      if (generation !== listGeneration.current) return;
      listedQuery.current = query;
      setChatHistory(data);
      setNextToken(data.next_token || null);
      setActiveSessionIndex(0);
    } catch (err) {
      if (generation !== listGeneration.current) return;
      console.error(err);
      setChatHistory(null);
    } finally {
      if (generation === listGeneration.current) setIsLoadingChat(false);
    }
  };

  const loadMoreSessions = async () => {
    if (!selectedUserId || !nextToken) return;
    const generation = listGeneration.current;
    const query = listedQuery.current;
    try {
      setIsLoadingMore(true);
      const data = await apiService.getChatHistory(selectedUserId, {
        limit: SESSIONS_PAGE_SIZE,
        next_token: nextToken,
        // The token belongs to the order the list is in, which the studio
        // may have changed from the one asked for.
        order_by: listedOrder,
        lesson_id: query.lessonId,
        start_date: query.startDate,
        end_date: query.endDate,
      });
      if (generation !== listGeneration.current) return;
      setChatHistory((previous) =>
        previous
          ? { ...previous, sessions: [...previous.sessions, ...data.sessions] }
          : previous,
      );
      setNextToken(data.next_token || null);
    } catch (err) {
      if (generation === listGeneration.current) console.error(err);
    } finally {
      if (generation === listGeneration.current) setIsLoadingMore(false);
    }
  };

  const handleApplyFilters = () => {
    if (selectedUserId) {
      loadChatHistory(selectedUserId);
    }
  };

  const handleOrderByChange = (order: ChatHistoryOrder) => {
    setOrderBy(order);
    if (selectedUserId) {
      loadChatHistory(selectedUserId, false, order);
    }
  };

  const handleClearFilters = () => {
    setSelectedLesson(null);
    setStartDate("");
    setEndDate("");
    if (selectedUserId) {
      loadChatHistory(selectedUserId, true);
    }
  };

  const getSessionLabel = (session: Session, idx: number) => {
    // The label shows the time the list is ordered by.
    const sessionDateTime =
      sessionTime(session, listedOrder)?.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }) ?? "";
    const lessonLabel = session.lesson_name ? ` • ${session.lesson_name}` : "";
    const slideLabel =
      session.slide_index != null ? ` [Slide ${session.slide_index}]` : "";
    const notepadLabel = isNotepadSession(session) ? " • Notepad" : "";
    // Ordinals would read as "first session ever"; the list is newest-first.
    const dateLabel = sessionDateTime || `Session ${idx + 1}`;
    return `${dateLabel}${lessonLabel}${slideLabel}${notepadLabel}`;
  };

  const formatMessageTime = (session: Session) => {
    if (session.created_at) {
      return new Date(session.created_at).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      });
    }
    return "";
  };

  const selectedUser = users.find((u) => u.id === selectedUserId);

  return (
    <div className="flex h-full min-h-0 flex-1 gap-4 overflow-hidden max-lg:flex-col">
      <UserList
        users={users.filter((user) => (!userFilters.organization_id || user.organizationId === userFilters.organization_id) && (!userFilters.type || user.type === userFilters.type))}
        searchControls={<AdvancedOptions values={userFilters} onChange={setUserFilters} fields={[
          { key: "organization_id", label: "Organization", options: organizations.map((org) => ({ value: org.id, label: org.name })) },
          { key: "type", label: "Account role", options: [{ value: "teacher", label: "Teacher" }, { value: "student", label: "Student" }] },
        ]} />}
        isLoading={isLoadingUsers}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onUserSelect={(user) => setSelectedUserId(user.id)}
        getUserId={(user) => `${user.type}-${user.id}`}
        getDisplayName={(user) => user.name}
        isSelected={(user) => user.id === selectedUserId}
        getSubtitle={(user) => user.type}
        filterUser={(user, term) =>
          user.name.toLowerCase().includes(term.toLowerCase()) ||
          (user.email?.toLowerCase().includes(term.toLowerCase()) ?? false)
        }
      />

      <ChatPanel
        selectedUser={selectedUser}
        chatHistory={chatHistory}
        activeSessionIndex={activeSessionIndex}
        isLoadingChat={isLoadingChat}
        isLoadingMore={isLoadingMore}
        isFiltersExpanded={isFiltersExpanded}
        hasActiveFilters={hasActiveFilters}
        selectedLesson={selectedLesson}
        startDate={startDate}
        endDate={endDate}
        nextToken={nextToken}
        onFiltersExpandedChange={setIsFiltersExpanded}
        onSessionChange={setActiveSessionIndex}
        onLoadMoreSessions={loadMoreSessions}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onLessonChange={setSelectedLesson}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        orderBy={orderBy}
        listedOrder={listedOrder}
        onOrderByChange={handleOrderByChange}
        getSessionLabel={getSessionLabel}
        formatMessageTime={formatMessageTime}
      />
    </div>
  );
};
